<template>
  <div class="upload-file" @click="fileBoxClick" v-loading="loading" :style="{ height: style.height, width: style.width }">
    <input
      type="file"
      style="display: none"
      ref="fileRef"
      @change="handleChange"
    />
    <template v-if="!url">
      <div class="title" v-if="title">{{ $t(title) }}</div>
      <slot name="defaultImg" v-if="$slots.defaultImg"></slot>
      <img
        v-else
        style="height: 34px; width: 34px; display: block"
        :src="uploadIcon"
        alt
      />
      <div class="label">{{ $t(label) }}</div>
    </template>
    <template v-else>
      <div class="has-file-box">
        <div class="title" v-if="title">{{ title }}</div>
        <template v-if="['rar','zip','doc','docx','pdf','txt'].includes(getFileUrlType(url))">
          <div class="pdf">
            <img class="pdf-icon" src="./文件.png" />
            <div class="pdf-text">{{ getFileUrlName(url) }}</div>
          </div>
        </template>
        <template v-else-if="getFileUrlType(url) === 'mp4'">
          <video width="80" height="80" controls="false">
            <source :src="url" type="video/mp4" />
          </video>
        </template>
        <template v-else>
          <img class="img" :src="url" />
        </template>
        <div class="masking">
          <el-space>
            <!-- pdf查看 -->
            <template v-if="['rar','zip','doc','docx','pdf', 'txt'].includes(getFileUrlType(url))">
              <el-image
                class="icon-eye"
                :src="EyeIcon"
                fit="scale-down"
                @click="onPreview(url)"
              />
            </template>
            <!-- 视频文件查看 -->
            <template v-else-if="getFileUrlType(url) === 'mp4'">
              <el-image
                class="icon-eye"
                :src="EyeIcon"
                fit="scale-down"
                @click="$refs.videoModalRef.show({ src: url })"
              />
            </template>
            <!-- img查看 -->
            <template v-else>
              <el-image
                class="icon-eye"
                :src="EyeIcon"
                fit="scale-down"
                :preview-src-list="[url]"
              />
            </template>
            <el-image
              v-if="isShowDelete"
              class="icon-eye"
              :src="WhiteIcon"
              fit="scale-down"
              @click.stop="url = ''"
            />
          </el-space>
        </div>
      </div>
    </template>
    <!-- 裁剪 -->
    <CroppingModal ref="croppingModalRef" @cropping-success="croppingSuccess" />
    <!-- 视频播放 -->
    <VideoModal ref="videoModalRef" />
  </div>
</template>

<script setup>
import { computed, ref, nextTick } from 'vue'
import { uploadToken, uploadFileToQiniu } from '@/api/common'
import { ElMessage } from 'element-plus'
import CroppingModal from './cropping-modal.vue'
import VideoModal from './video-modal.vue'
import EyeIcon from '@/assets/icon/eye-icon.png'
import WhiteIcon from '@/assets/icon/delete-icon-white.png'
import { uploadUtil } from '@/utils/utils'
import {
  getObjectURL,
  getFileUrlType,
  getFileUrlName,
  generateRandomString,
} from '@/utils/tools'
import { $t } from '@/i18n'

const props = defineProps({
  // 绑定的值
  modelValue: {
    type: String,
    required: true,
    default: '',
  },
  style: {
    type: Object,
    default: {
      width: '96px',
      height: '96px'
    }
  },
  // 头部标题
  title: {
    type: String,
    default: '',
  },
  // label
  label: {
    type: String,
    default: $t('common.uploadImage'),
  },
  // 是否显现删除按钮
  isShowDelete: {
    type: Boolean,
    default: true,
  },
  // 上传图片
  uploadIcon: {
    type: String,
    default: 'https://wrshg-goods-pic.oss-cn-beijing.aliyuncs.com/1568-1735125916481-camera.png',
  },
  // 文件类型，默认为图片
  // 格式为 png/jgp/mp4/......
  fileType: {
    type: String,
    default: 'png/jpeg/jpg',
  },
  // 最大文件大小(mb单位)
  fileSize: {
    type: Number,
    default: 100,
  },
  // 文件七牛云路径
  fileQiNiuYunPath: {
    type: String,
    default: 'image/new-goods',
  },
  // 图片是否需要裁剪
  isCropping: {
    type: Boolean,
    default: false,
  },
  // 裁剪配置参数option
  croppingOptions: {
    // 参数配置地址 https://github.com/xyxiao001/vue-cropper
    type: Object,
    default: {
      autoCropWidth: 500,
      autoCropHeight: 500,
    },
  },
  // 视频比例
  videoRatio: {
    type: String,
    //传"16 / 9"，默认没有限制
    default: '',
  },
  // 视频最大时间
  videoMaxTime: {
    type: Number,
    // 单位秒,默认最大两个小时
    default: 60 * 60 * 1,
  },
})
const emit = defineEmits(['update:modelValue'])

const fileRef = ref(null)
const loading = ref(false)
const croppingModalRef = ref(null)
const videoModalRef = ref(null)

const url = computed({
  get() {
    return props.modelValue
  },
  set(value) {
    emit('update:modelValue', value)
  },
})

function fileBoxClick() {
  if (!url.value) {
    fileRef.value.click()
  } else {
  }
}

async function getUploadToken() {
  const { result } = await uploadToken()
  if (result) {
    return result.data.upload_token
  }
  return false
}

// 上传文件 获取路径结果
async function requests(file) {
  const token = await getUploadToken()
  if (token) {
    const formData = new FormData()
    formData.set('file', file)
    formData.set(
      'key',
      `${props.fileQiNiuYunPath}/${Date.now()}/${
        file.name ? file.name : generateRandomString() + '.png'
      }`
    )
    formData.set('token', token)
    const file_url = await uploadFileToQiniu(formData)
    if (file_url) {
      return file_url
    }
    return false
  }
  return false
}

// 对上传后的结果进行处理
async function requestFile(file) {
  loading.value = true
  const _url = await uploadUtil(file)
  if(_url) {
    url.value = _url
  }
  // const { result } = await uploadUtil(file)
  // if (result) {
  //   ElMessage.success(`上传成功`)
  //   await nextTick()
  //   url.value = result.data.url
  //   console.log(result.data.url, 'result.data.url')
  //   console.log(url.value, 'url')
  // } else {
  //   ElMessage.error('上传失败')
  // }
  loading.value = false
}

// 文件上传前置校验
async function fileUploadVerify(files) {
  if (!files.length) {
    return `文件选择失败，请重新选择文件！`
  }
  // 默认获取第一个文件
  let currentFile = files[0]
  let fileType = currentFile.name.split('.').at(-1).toLowerCase()
  let allowFileTypeList = props.fileType.split('/')

  if (!allowFileTypeList.includes(fileType)) {
    return $t('commonComponents.uploadImgErr1', [allowFileTypeList.join(',')])
  }
  if (currentFile.size >= props.fileSize * 1024 * 1024) {
    return $t('commonComponents.uploadImgErr2', [props.fileSize])
  }
  if (fileType === 'mp4') {
    const video = document.createElement('video')
    const objectURL = URL.createObjectURL(files[0])
    video.src = objectURL
    return new Promise((resolve, reject) => {
      video.addEventListener('loadedmetadata', () => {
        // 判断宽高比例是否为16:9
        if (props.videoRatio) {
          const aspectRatio = video.videoWidth / video.videoHeight
          let limitRationNumber =
            props.videoRatio.split('/')[0] / props.videoRatio.split('/')[1]
          if (Math.abs(aspectRatio - limitRationNumber) > 0.1) {
            resolve(
              $t('commonComponents.uploadImgErr3', [video.videoWidth, video.videoHeight, props.videoRatio])
            )
          } else {
            resolve(false)
          }
        }
        // 判断时间小于2小时
        if (video.duration > props.videoMaxTime) {
          resolve(
            $t('commonComponents.uploadImgErr4', [video.duration, props.videoMaxTime])
          )
        } else {
          resolve(false)
        }
        // 释放资源
        URL.revokeObjectURL(objectURL)
      })
    })
  } else {
    return false
  }
}

const handleChange = async (e) => {
  const {
    target: { files },
  } = e
  // 文件校验
  let errorContent = await fileUploadVerify(files)
  if (errorContent) {
    ElMessage.error(errorContent)
    e.target.value = null
    return
  }
  // 图片是否需要是否需要裁剪
  if (props.isCropping) {
    let options = {
      img: getObjectURL(files[0]),
      outputType: 'png',
      autoCrop: true,
      fixedBox: true, //裁剪框尺寸固定不变
      canMoveBox: false, //禁止拖动裁剪框
      canMove: true, //允许拖动底层图片
      full: true,
      // autoCropWidth: 250,
      // autoCropHeight: 250,
      ...props.croppingOptions,
    }
    croppingModalRef.value.toggleVisible(options)
  } else {
    requestFile(files[0])
  }
  e.target.value = null
}

// 获取裁剪的图片
async function croppingSuccess(blob, finishConfirmLoading) {
  try {
    await requestFile(blob)
    croppingModalRef.value?.close?.()
  } finally {
    finishConfirmLoading?.()
  }
}

function onPreview(val) {
  window.open(val)
}
</script>

<style lang="scss" scoped>
.upload-file {
  position: relative;
  background: #f2f3f5;
  border-radius: 8px;
  border: 1px solid #e5e6eb;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  .title {
    position: absolute;
    top: 0;
    left: 0;
    height: 16px;
    line-height: 16px;
    width: 100%;
    font-size: 11px;
    background: #447bf7;
    border-radius: 8px 6px 0px 0px;
    text-align: center;
    color: #ffffff;
  }
  .label {
    margin-top: 5px;
    line-height: 16px;
    font-size: 13px;
    font-weight: 400;
    color: #787d8a;
    text-align: center;
  }
  .has-file-box {
    width: 100%;
    height: 100%;
    position: relative;

    .pdf {
      .pdf-icon {
        width: 24px;
        height: 29px;
        display: block;
        padding: 12px auto 0;
        margin: 0 auto;
        padding-top: 12px;
      }
      .pdf-text {
        margin-top: 6px;
        font-size: 11px;
        text-align: center;
        width: 100%; /* 设置容器宽度 */
        display: -webkit-box;
        overflow: hidden;
        -webkit-box-orient: vertical;
        text-overflow: ellipsis;
        /* 控制显示的行数 */
        -webkit-line-clamp: 2;
        /* 设置行高，这个值需要和你的字体大小相适应 */
        line-height: 1.2em;
        max-height: 2.4em; /* 行高乘以行数，确保不会出现半个行的情况 */
      }
    }
    video {
      border-radius: 8px;
    }
    .img {
      width: 100%;
      height: 100%;
      border-radius: 8px;
      object-fit: scale-down;
    }
    .masking {
      display: none;
      align-items: center;
      justify-content: center;
      position: absolute;
      left: 0;
      top: 0;
      z-index: 200;
      width: 100%;
      height: 100%;
      border-radius: 8px;
      background: rgba(0, 0, 0, 0.4);
      .icon-eye {
        width: 16px;
        height: 16px;
        cursor: pointer;
      }
    }
  }
  .has-file-box:hover {
    .masking {
      display: flex;
    }
  }
  .remove {
    position: absolute;
    right: -5px;
    top: -3px;
    z-index: 100;
  }
}
</style>
